import { useEffect, useRef } from 'react';
import { pushToast } from '../../lib/toast';

declare global {
  interface Window {
    daum?: { Postcode: new (opts: PostcodeOptions) => { embed: (el: HTMLElement) => void } };
  }
}

interface PostcodeData {
  zonecode: string;
  address: string;
  addressType?: 'R' | 'J';
}
interface PostcodeOptions {
  oncomplete: (data: PostcodeData) => void;
  onclose?: () => void;
  width?: string;
  height?: string;
}

const SCRIPT_SRC = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
let scriptPromise: Promise<void> | null = null;

function ensureScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('window 없음'));
  if (window.daum?.Postcode) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error('우편번호 스크립트를 불러올 수 없습니다.'));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onComplete: (data: { zonecode: string; address: string }) => void;
}

export function PostcodeModal({ open, onClose, onComplete }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.daum) return;
        containerRef.current.innerHTML = '';
        new window.daum.Postcode({
          oncomplete: (data) => {
            onComplete({ zonecode: data.zonecode, address: data.address });
            onClose();
          },
          onclose: onClose,
          width: '100%',
          height: '100%',
        }).embed(containerRef.current);
      })
      .catch((err: Error) => {
        pushToast('error', err.message);
        onClose();
      });
    return () => {
      cancelled = true;
    };
  }, [open, onClose, onComplete]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/40 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="우편번호 검색"
    >
      <div
        className="flex w-full max-w-[480px] flex-col overflow-hidden rounded-t-2xl bg-paper shadow-modal sm:rounded-2xl"
        style={{ height: 'min(560px, 80vh)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-ink-200 px-4 py-3">
          <h3 className="text-[14px] font-medium text-ink-900">우편번호 검색</h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            aria-label="닫기"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div ref={containerRef} className="min-h-0 flex-1" />
      </div>
    </div>
  );
}
